<div class="d-flex analysis-layout">
  <!-- Left Sidebar Navigation -->
  <app-analysis-nav [analysisType]="analysisType" [config]="navConfig"> </app-analysis-nav>

  <!-- Main Content -->
  <div class="content-area">
    <h2>Project Summary Statistics</h2>

    <div>
      <h4>Average read coverage in locus</h4>
      <div class="table-actions" role="group" aria-label="Export coverage statistics">
        <button
          class="btn btn-outline-secondary"
          type="button"
          [disabled]="!projectViewData.coverage_data.length"
          (click)="copyTable('coverage')"
        >
          Copy
        </button>
        <button
          class="btn btn-outline-secondary"
          type="button"
          [disabled]="!projectViewData.coverage_data.length"
          (click)="exportExcel('coverage')"
        >
          Excel
        </button>
        <button
          class="btn btn-outline-secondary"
          type="button"
          [disabled]="!projectViewData.coverage_data.length"
          (click)="exportPdf('coverage')"
        >
          PDF
        </button>
      </div>
      @if (exportMessages.coverage) {
        <p class="export-message" role="status">{{ exportMessages.coverage }}</p>
      }
      <div class="global-filter">
        <label class="visually-hidden" for="gene-coverage-global-filter">Search coverage statistics</label>
        <input
          id="gene-coverage-global-filter"
          class="form-control"
          type="search"
          placeholder="Search all columns"
          [value]="coverageGlobalFilter"
          (input)="onGlobalFilterChange('coverage', $event, coverageTable)"
        />
      </div>
      <div class="row">
        <div class="table-container">
          <p-table
            #coverageTable
            [value]="projectViewData.coverage_data"
            [columns]="projectViewData.coverage_cols"
            [filters]="coverageFilters"
            [globalFilterFields]="coverageGlobalFilterFields"
            (onFilter)="onTableFilter('coverage', $event)"
            [paginator]="true"
            [rows]="10"
            [rowsPerPageOptions]="pageSizes"
            [showCurrentPageReport]="true"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            class="gene-summary-table"
          >
            <ng-template pTemplate="header" let-columns>
              <tr>
                @for (column of columns; track column.data) {
                  <th [pSortableColumn]="column.data">
                    <div class="table-header-content">
                      <span class="table-header-title">
                        {{ isReportsColumn(column) ? 'Reports' : column.title }}
                        <p-sortIcon [field]="column.data"></p-sortIcon>
                      </span>
                      @if (!isReportsColumn(column)) {
                        <p-columnFilter
                          [field]="column.data"
                          [type]="isCoverageNumericColumn(column) ? 'numeric' : 'text'"
                          display="menu"
                          [showOperator]="true"
                          [showMatchModes]="true"
                          [showAddButton]="true"
                          [filterButtonProps]="filterButtonProps"
                        />
                      }
                    </div>
                  </th>
                }
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-rowData let-columns="columns">
              <tr>
                @for (column of columns; track column.data) {
                  <td>
                    @if (getDocLinks(rowData, column.data); as docLinks) {
                      @if (docLinks.length > 0) {
                        @for (link of docLinks; track link.dataLink) {
                          <button
                            class="btn btn-secondary btn-sm doc-link-button"
                            type="button"
                            [attr.data-link]="link.dataLink"
                            [attr.data-sample]="link.sample"
                            (click)="openDocLink($event)"
                          >
                            {{ link.label }}
                          </button>
                        }
                      } @else {
                        {{ rowData[column.data] }}
                      }
                    }
                  </td>
                }
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage" let-columns>
              <tr>
                <td [attr.colspan]="columns.length || 1">No coverage statistics available.</td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      </div>
    </div>

    <div>
      <h4>Gene count</h4>
      <div class="table-actions" role="group" aria-label="Export gene count statistics">
        <button
          class="btn btn-outline-secondary"
          type="button"
          [disabled]="!projectViewData.gene_counts.length"
          (click)="copyTable('geneCounts')"
        >
          Copy
        </button>
        <button
          class="btn btn-outline-secondary"
          type="button"
          [disabled]="!projectViewData.gene_counts.length"
          (click)="exportExcel('geneCounts')"
        >
          Excel
        </button>
        <button
          class="btn btn-outline-secondary"
          type="button"
          [disabled]="!projectViewData.gene_counts.length"
          (click)="exportPdf('geneCounts')"
        >
          PDF
        </button>
      </div>
      @if (exportMessages.geneCounts) {
        <p class="export-message" role="status">{{ exportMessages.geneCounts }}</p>
      }
      <div class="global-filter">
        <label class="visually-hidden" for="gene-counts-global-filter">Search gene count statistics</label>
        <input
          id="gene-counts-global-filter"
          class="form-control"
          type="search"
          placeholder="Search all columns"
          [value]="geneCountsGlobalFilter"
          (input)="onGlobalFilterChange('geneCounts', $event, geneCountsTable)"
        />
      </div>
      <div class="row">
        <div class="table-container">
          <p-table
            #geneCountsTable
            [value]="projectViewData.gene_counts"
            [columns]="projectViewData.gene_counts_cols"
            [filters]="geneCountsFilters"
            [globalFilterFields]="geneCountsGlobalFilterFields"
            (onFilter)="onTableFilter('geneCounts', $event)"
            [paginator]="true"
            [rows]="10"
            [rowsPerPageOptions]="pageSizes"
            [showCurrentPageReport]="true"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            class="gene-summary-table"
          >
            <ng-template pTemplate="header" let-columns>
              <tr>
                @for (column of columns; track column.data) {
                  <th [pSortableColumn]="column.data">
                    <div class="table-header-content">
                      <span class="table-header-title">
                        {{ column.title }}
                        <p-sortIcon [field]="column.data"></p-sortIcon>
                      </span>
                      <p-columnFilter
                        [field]="column.data"
                        [type]="isGeneCountsNumericColumn(column) ? 'numeric' : 'text'"
                        display="menu"
                        [showOperator]="true"
                        [showMatchModes]="true"
                        [showAddButton]="true"
                        [filterButtonProps]="filterButtonProps"
                      />
                    </div>
                  </th>
                }
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-rowData let-columns="columns">
              <tr>
                @for (column of columns; track column.data) {
                  <td>{{ rowData[column.data] }}</td>
                }
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage" let-columns>
              <tr>
                <td [attr.colspan]="columns.length || 1">No gene count statistics available.</td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      </div>
    </div>
  </div>
</div>
